
///
/// EarthBrowser
///
/// Created by Matt Giger on 2/8/2025
/// Copyright (c) 2025 Geodata Labs
///

import { EarthBrowserCanvas } from '@/components/EarthBrowserCanvas'
import { CameraControls } from "@/components/controls/CameraControls"
import { LayersPanel } from '@/components/controls/layers-panel/LayersPanel'
import { CommandPalette } from '@/components/controls/search/CommandPalette'
import { TimelineBar } from "@/components/controls/TimelineBar"
import {
	AlertBrowserFeature,
	BuoyBrowserFeature,
	EarthquakeBrowserFeature,
	HurricaneBrowserFeature,
	LocationBrowserFeature,
	RadarBrowserFeature,
	SatelliteBrowserFeature,
	ViewsBrowserFeature,
	VolcanoBrowserFeature,
	WebcamBrowserFeature,
	WildfireBrowserFeature
} from '@/components/features'
import { ImageOverlayBrowserFeature } from '@/components/features/image-overlay-browser/ImageOverlayBrowserFeature'
import { SQLTerminalBrowserFeature } from '@/components/features/sql-terminal-browser/SQLTerminalBrowserFeature'
import { StorageManagerBrowserFeature } from '@/components/features/storage-manager-browser/StorageManagerBrowserFeature'
import { CheckoutStatusOverlay } from "@/components/misc/CheckoutStatusOverlay"
import { ConversionBanner } from "@/components/misc/ConversionBanner"
import { NotificationSlot } from "@/components/misc/NotificationSlot"
import { SharedContentBanner } from "@/components/misc/SharedContentBanner"
import { FullAccessOfferCard } from "@/components/misc/FullAccessOfferCard"
import { LoadStatusAnnouncer } from "@/components/misc/LoadStatusAnnouncer"
import { TitleTooltipLayer } from "@/components/ui/title-tooltip"
import { SavedViewPrompt } from "@/components/misc/SavedViewPrompt"
import { SaveViewToast } from "@/components/misc/SaveViewToast"
import { StoryCard } from "@/components/misc/StoryCard"
import { StoryEditor } from "@/components/features/story-editor/StoryEditor"
import { SignInWall } from "@/components/misc/SignInWall"
import { SupporterOfferCard } from "@/components/misc/SupporterOfferCard"
import { SupportInterstitial } from "@/components/misc/SupportInterstitial"
import { useEngagementSession } from '@/hooks/useEngagementSession'
import { useEntitlementsSync } from '@/hooks/useEntitlements'
import { useCommandPaletteShortcuts } from '@/hooks/useCommandPaletteShortcuts'
import { useLocalDataDirectoryScanner } from '@/hooks/useLocalDataDirectoryScanner'
import { usePlacemarksSync } from '@/hooks/usePlacemarksSync'
import { useSavedViewsSync } from '@/hooks/useSavedViewsSync'
import { useStoriesSync } from '@/hooks/useStoriesSync'
import { useTimeCameraShortcuts } from '@/hooks/useTimeCameraShortcuts'
import { LAYERS } from '@/components/controls/layers-panel/layerRegistry'
import { cStoryAuthoringEnabled } from '@/utils/featureGates'
import { parseStoryTarget, parseTarget } from '@/utils/navigationHistory'
import { sendCameraCommand, sendDatasetAction } from "@/redux/earthBrowserSlice"
import { appStore, useAppDispatch } from '@/redux/store'
import type { DatasetType } from '@/types/DatasetElement'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from "react-router-dom"
import { TopLeftControls } from "../header/TopLeftControls"
import { TopRightControls } from "../header/TopRightControls"

export function PageContent() {
	const dispatch = useAppDispatch()

	// Auto-scan local data directory on launch
	useLocalDataDirectoryScanner()

	// Global time/camera keyboard shortcuts (space, arrows, n, +/-)
	useTimeCameraShortcuts()

	// Saved views: delta sync on sign-in / app start / reconnect
	useSavedViewsSync()

	// Placemarks: same delta-sync triggers as saved views
	usePlacemarksSync()

	// Story tours: same delta-sync triggers as saved views
	useStoriesSync()

	// Conversion-funnel bookkeeping: session counting, deep-link detection,
	// durable anonymous id cookie refresh
	useEngagementSession()

	// Mirror the resolved subscription entitlement into the non-persisted
	// entitlements slice for engine-side consumers (radar frame limits)
	useEntitlementsSync()

	// Dev tools unlock state (unlocked by long-pressing the account pill)
	const [devtoolsUnlocked, setDevtoolsUnlocked] = useState(false)

	// Cmd/Ctrl-K and Cmd/Ctrl-P open the command palette
	useCommandPaletteShortcuts()

	// URL state for navigation
	const [searchParams, setSearchParams] = useSearchParams()

	// Launch ?layers= deep link: enable-only layer dependencies from a
	// shared view URL. Never disables anything the user has on; unknown
	// names are ignored. Runs once - later param changes are navigation.
	const layersParamHandled = useRef(false)
	useEffect(() => {
		if (layersParamHandled.current) return
		layersParamHandled.current = true

		const layersParam = new URL(window.location.href).searchParams.get("layers")
		if (!layersParam) return

		const validTypes = new Set(LAYERS.map(layer => layer.visibility).filter(Boolean))
		for (const name of layersParam.split(",")) {
			const type = name.trim() as DatasetType
			if (validTypes.has(type)) {
				dispatch(sendDatasetAction({ type, action: "enable" }))
			}
		}
	}, [dispatch])

	// Last target value this component acted on. Guards against re-dispatching
	// a selection when an unrelated search param changes (e.g. login/error),
	// which would restart the focus transition the user didn't ask for.
	const lastHandledTarget = useRef<string | null>(null)
	const isFirstTargetRun = useRef(true)

	// Listen for navigation events from non-React code (3D entity focus/defocus).
	// A null target means focus was released: remove the param so the URL stops
	// claiming an entity is focused. That removal pushes a history entry, so
	// Back returns to the previously focused entity.
	useEffect(() => {
		const handleNavigate = (event: CustomEvent<{ target: string | null }>) => {
			const value = event.detail.target

			// Removing an already-absent target would push a duplicate history entry
			if (!value && !new URL(window.location.href).searchParams.has("target")) {
				return
			}

			setSearchParams(prev => {
				const newParams = new URLSearchParams(prev)
				if (value) {
					newParams.set("target", value)
				} else {
					newParams.delete("target")
				}
				return newParams
			})
		}

		window.addEventListener('earthbrowser:navigate', handleNavigate as EventListener)
		return () => {
			window.removeEventListener('earthbrowser:navigate', handleNavigate as EventListener)
		}
	}, [setSearchParams])

	// Handle URL target parameter changes (browser back/forward navigation).
	// Note: the launch deep-link is handled by the engine itself once it starts
	// (see EarthBrowserActivity.focusInitialTarget); this effect covers
	// navigation while the app is already running.
	useEffect(() => {
		const isFirstRun = isFirstTargetRun.current
		isFirstTargetRun.current = false

		const target = searchParams.get('target')
		if (target === lastHandledTarget.current) return
		lastHandledTarget.current = target

		if (!target) {
			// Target removed (e.g. Back to the pre-focus entry): release focus
			// so the view matches the URL instead of staying on the old entity
			dispatch(sendDatasetAction({ type: '', action: "deselect" }))
			return
		}

		// On the first run the engine isn't subscribed yet; the launch deep-link
		// is handled by focusInitialTarget() instead (the bridge has no replay)
		const storyTarget = parseStoryTarget(target)
		if (storyTarget) {
			if (isFirstRun) return
			// The player pushed this target itself when playback started;
			// don't restart the story that's already running
			if (appStore.getState().dialog.activeStory?.storyId === storyTarget.storyId) return
			dispatch(sendCameraCommand({
				command: "story/play",
				data: { storyId: storyTarget.storyId, stopIndex: storyTarget.stopIndex },
			}))
			return
		}

		const parsed = parseTarget(target)
		if (!parsed) return

		if (isFirstRun) return

		if (parsed.kind === 'coords') {
			// Temporary location focus (OGC features, etc.); geoid 0 means temporary
			dispatch(sendDatasetAction({
				type: 'location',
				action: "selectCoords",
				id: '0',
				lat: parsed.lat,
				lon: parsed.lon,
				name: parsed.name
			}))
		} else {
			// Dispatch the selection action to navigate to the entity
			dispatch(sendDatasetAction({
				type: parsed.type,
				action: "selectId",
				id: parsed.id
			}))
		}
	}, [searchParams, dispatch])

	return (
		<div className="fixed inset-0">

			<div>
				<EarthBrowserCanvas />

				{/* Feature-based browsers (self-contained with command registration) */}
				<VolcanoBrowserFeature />
				<ViewsBrowserFeature />
				<EarthquakeBrowserFeature />
				<AlertBrowserFeature />
				<LocationBrowserFeature />
				<SatelliteBrowserFeature />
				<BuoyBrowserFeature />
				<WebcamBrowserFeature />
				<WildfireBrowserFeature />
				<HurricaneBrowserFeature />
				<RadarBrowserFeature />
				<SQLTerminalBrowserFeature devtoolsUnlocked={devtoolsUnlocked} />
				<ImageOverlayBrowserFeature />
				<StorageManagerBrowserFeature devtoolsUnlocked={devtoolsUnlocked} />

				{/* Layers + Search launchers; the Layers panel (catalog) and the
				    ⌘K palette (search over everything) render themselves */}
				<TopLeftControls />
				<LayersPanel />
				<CommandPalette />

				<TimelineBar />
				<CameraControls />
				{/* Views / Share pair + account menu */}
				<TopRightControls onDevtoolsUnlock={() => setDevtoolsUnlocked(true)} />

				{/* Single top-center slot for transient notices: highest
				    priority claimant renders, so they never stack. Members:
				    shared-content banner, save-view rename toast, one-time
				    saved-view sync prompt (second anonymous save), signed-in
				    full-access offer, and the rung-1 conversion banner
				    (sessions 2-3). */}
				<NotificationSlot>
					<SharedContentBanner />
					<SaveViewToast />
					<SavedViewPrompt />
					<FullAccessOfferCard />
					<ConversionBanner />
				</NotificationSlot>

				{/* Modal rungs of the conversion ladder (engagementSlice): the
				    support interstitial at rung 2 (4+ sessions or 7+ days,
				    never on deep-link sessions, once per day) and the required
				    sign-in wall past the engagement threshold (deep-link
				    sessions exempt) */}
				<SupportInterstitial />
				<SignInWall />

				{/* Founding-supporter offer redemption (email deep link only) */}
				<SupporterOfferCard />

				{/* Story playback card (title/body, progress, prev/next, resume) */}
				<StoryCard />

				{/* Story authoring panel (capture-based stops + annotations) */}
				{cStoryAuthoringEnabled && <StoryEditor />}

				{/* Post-Stripe-checkout success/cancelled card */}
				<CheckoutStatusOverlay />

				{/* Polite live region: "Loading clouds and radar" for screen
				    readers while visible layers fetch (visual rings live on
				    the Layers button, rows, legend chips and the timeline) */}
				<LoadStatusAnnouncer />

				{/* Every `title` attribute in the chrome renders through this
				    one glass tooltip instead of the OS tooltip */}
				<TitleTooltipLayer />
			</div>
		</div>
	)
}